import { HttpClient, robotoHeaders } from "@/shared/http";
import { type PaginatedResponse } from "@/shared/http";

import {
  type CreateTriggerV2Request,
  type EventSamplesResponse,
  type TriggerDispatchRecord,
  type TriggerV2Record,
  type UpdateTriggerV2Request,
} from "./TriggerV2Record";

interface Options {
  abortSignal?: AbortSignal;
}

interface PageOptions extends Options {
  limit?: number;
  pageToken?: string;
}

/**
 * The unified trigger surface (``/v1/triggers`` at the latest API version):
 * every trigger — v2-native or legacy — in the v2 wire shape.
 *
 * Legacy (``engine: "v1"``) rows are editable only within legacy capabilities;
 * the backend rejects non-representable edits with an actionable 400 whose
 * message callers should surface verbatim.
 */
export class TriggerV2Service {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createTrigger(
    request: CreateTriggerV2Request,
    orgId: string,
    options?: Options,
  ): Promise<TriggerV2Record> {
    const requestUrl = this.#httpClient.constructUrl("v1/triggers");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });
    return response.json<TriggerV2Record>();
  }

  public async listTriggers(
    orgId: string,
    options?: Options,
  ): Promise<TriggerV2Record[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/triggers");
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: orgId }),
    });
    const page = await response.json<PaginatedResponse<TriggerV2Record>>();
    return page.items;
  }

  public async getTriggerById(
    triggerId: string,
    options?: Options,
  ): Promise<TriggerV2Record> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/id/${encodeURIComponent(triggerId)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<TriggerV2Record>();
  }

  public async getTriggerByName(
    name: string,
    orgId: string,
    options?: Options,
  ): Promise<TriggerV2Record> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/${encodeURIComponent(name)}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: orgId }),
    });
    return response.json<TriggerV2Record>();
  }

  public async updateTrigger(
    name: string,
    updates: UpdateTriggerV2Request,
    orgId: string,
    options?: Options,
  ): Promise<TriggerV2Record> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/${encodeURIComponent(name)}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: orgId }),
      body: JSON.stringify(updates),
    });
    return response.json<TriggerV2Record>();
  }

  public async setEnabled(
    name: string,
    enabled: boolean,
    orgId: string,
    options?: Options,
  ): Promise<TriggerV2Record> {
    return this.updateTrigger(name, { enabled }, orgId, options);
  }

  public async deleteTrigger(
    name: string,
    orgId: string,
    options?: Options,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/${encodeURIComponent(name)}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: orgId }),
    });
    return response.throwIfError();
  }

  /** One keyset page of a trigger's dispatches, newest first. */
  public async getTriggerDispatches(
    triggerId: string,
    options?: PageOptions,
  ): Promise<PaginatedResponse<TriggerDispatchRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/id/${encodeURIComponent(triggerId)}/dispatches`,
      TriggerV2Service.#pageParams(options),
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<TriggerDispatchRecord>>();
  }

  /** One keyset page of every dispatch whose subject is this dataset, newest first. */
  public async getDatasetDispatches(
    datasetId: string,
    orgId: string,
    options?: PageOptions,
  ): Promise<PaginatedResponse<TriggerDispatchRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/triggers/dataset/id/${encodeURIComponent(datasetId)}/dispatches`,
      TriggerV2Service.#pageParams(options),
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: orgId }),
    });
    return response.json<PaginatedResponse<TriggerDispatchRecord>>();
  }

  /**
   * A realistic, dereferenced sample of every event type: what a condition or
   * target template can reference, with values. Static on the server; cache it
   * for the session.
   */
  public async getEventSamples(
    options?: Options,
  ): Promise<EventSamplesResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/triggers/events/samples",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<EventSamplesResponse>();
  }

  static #pageParams(options?: PageOptions): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    return searchParams;
  }
}
